import { loadBaseCSS } from '../shared/button-utils.js';
import {
  clearActiveContentDragHover,
  clearActiveContentDragState,
  getActiveContentDragHover,
  getActiveContentDragPayload,
  getContentDragPayload,
  setActiveContentDragHover
} from '../content-blocks/qds-content-drag.js';
import '../content-block-frame/qds-content-block-frame.js';
import {
  findBlockById,
  findBlockTreeLocation,
  getBlockChildOptions,
  getBlockChildren,
  normalizeContentDocument,
  resolveRegistry,
  sanitizeHtml,
  sortBlocksForMode
} from '../content-blocks/qds-content-blocks.js';
import type { QdsContentDocument } from '../../types/content.js';
import type { QdsBlockRegistry, QdsRenderContext } from '../../types/blocks.js';
import type { QdsBlockDropDetail, QdsBlockDropTarget } from '../../types/designer.js';
import type { QdsContentDragPayload } from '../content-blocks/qds-content-drag.js';
import type QdsContentBlockFrame from '../content-block-frame/qds-content-block-frame.js';

loadBaseCSS();

const ROOT_PARENT_ID = '__root__';

interface ResolvedDropDetail {
  signature: string;
  target: QdsBlockDropTarget;
  kind: 'before' | 'after' | 'tail' | 'empty' | 'container' | 'dashboard';
  element: HTMLElement;
}

let canvasDragOwnerCounter = 0;

function encodeParentBlockId(parentBlockId: string | null): string {
  return parentBlockId || ROOT_PARENT_ID;
}

function decodeParentBlockId(value: string | null | undefined): string | null {
  return value && value !== ROOT_PARENT_ID ? value : null;
}

function toIndex(value: string | null | undefined): number | null {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function containsPoint(element: HTMLElement, clientX: number, clientY: number): boolean {
  const rect = element.getBoundingClientRect();
  return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
}

function getDepth(element: HTMLElement): number {
  return Number.parseInt(element.dataset.depth || '0', 10) || 0;
}

function getArea(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  return rect.width * rect.height;
}

function getDropDetailPriority(detail: ResolvedDropDetail): number {
  switch (detail.kind) {
    case 'tail':
      return 0;
    case 'empty':
      return 1;
    case 'before':
    case 'after':
      return 2;
    case 'container':
      return 3;
    case 'dashboard':
      return 4;
    default:
      return 99;
  }
}

/**
 * @element qds-content-canvas
 *
 * The interactive editing surface that renders content blocks as draggable frames, resolving drag-and-drop targets for both page-flow and dashboard-grid layouts.
 *
 * @attr {boolean} readonly - When present, disables drag-and-drop editing on the canvas
 * @attr {string} selected-block-id - Id of the block currently selected on the canvas
 *
 * @property {QdsContentDocument | null} document - The content document to render
 * @property {QdsBlockRegistry | null} registry - The block registry used to resolve block definitions
 * @property {QdsRenderContext | undefined} context - Host-supplied render context (registry, capabilities)
 * @property {string | null} selectedBlockId - Id of the currently selected block
 * @property {boolean} readonly - Whether drag-and-drop editing is disabled
 *
 * @fires block-drop - Fired when a block is dropped onto a resolved target, carrying the drag item and target location
 */
export class QdsContentCanvas extends HTMLElement {
  private _document: QdsContentDocument | null = null;
  private _registry: QdsBlockRegistry | null = null;
  private _context?: QdsRenderContext;
  private _selectedBlockId: string | null = null;
  private _readonly = false;
  private _activeDropDetail: ResolvedDropDetail | null = null;
  private readonly _dragOwnerId = `canvas-${++canvasDragOwnerCounter}`;
  private _pointerDragBlockId: string | null = null;
  private _pointerDragStartX: number | null = null;
  private _pointerDragStartY: number | null = null;

  static get observedAttributes(): string[] {
    return ['readonly', 'selected-block-id'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    if (!this.isConnected || !this.shadowRoot) {
      return;
    }

    this.render();
    window.addEventListener('dragend', this._handleGlobalDragEnd as EventListener);
    window.addEventListener('drop', this._handleGlobalDragEnd as EventListener);
    this.shadowRoot.addEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot.addEventListener('drop', this._handleDrop as EventListener);
    this.shadowRoot.addEventListener('block-pointer-drag-start', this._handlePointerDragEvent as EventListener);
    this.shadowRoot.addEventListener('block-pointer-drag-move', this._handlePointerDragEvent as EventListener);
    this.shadowRoot.addEventListener('block-pointer-drag-end', this._handlePointerDragEvent as EventListener);
  }

  disconnectedCallback(): void {
    window.removeEventListener('dragend', this._handleGlobalDragEnd as EventListener);
    window.removeEventListener('drop', this._handleGlobalDragEnd as EventListener);
    this.shadowRoot?.removeEventListener('dragover', this._handleDragOver as EventListener);
    this.shadowRoot?.removeEventListener('drop', this._handleDrop as EventListener);
    this.shadowRoot?.removeEventListener('block-pointer-drag-start', this._handlePointerDragEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-pointer-drag-move', this._handlePointerDragEvent as EventListener);
    this.shadowRoot?.removeEventListener('block-pointer-drag-end', this._handlePointerDragEvent as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    if (name === 'readonly') {
      this._readonly = newValue !== null;
    }

    if (name === 'selected-block-id') {
      this._selectedBlockId = newValue;
    }

    this.render();
  }

  get document(): QdsContentDocument | null {
    return this._document;
  }

  set document(value: QdsContentDocument | null) {
    this._document = value;
    this.render();
  }

  get registry(): QdsBlockRegistry | null {
    return this._registry;
  }

  set registry(value: QdsBlockRegistry | null) {
    this._registry = value;
    this.render();
  }

  get context(): QdsRenderContext | undefined {
    return this._context;
  }

  set context(value: QdsRenderContext | undefined) {
    this._context = value;
    this.render();
  }

  get selectedBlockId(): string | null {
    return this._selectedBlockId;
  }

  set selectedBlockId(value: string | null) {
    this._selectedBlockId = value;
    if (value) {
      this.setAttribute('selected-block-id', value);
    } else {
      this.removeAttribute('selected-block-id');
    }
  }

  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    if (this._readonly) {
      this.setAttribute('readonly', '');
    } else {
      this.removeAttribute('readonly');
    }
  }

  private _getRegistry(): QdsBlockRegistry {
    return resolveRegistry(this._registry || this._context?.blockRegistry || null);
  }

  private _getDocumentModel(): QdsContentDocument | null {
    if (!this._document) {
      return null;
    }

    return normalizeContentDocument(this._document, this._getRegistry());
  }

  private _pickDeepestTarget(selector: string, clientX: number, clientY: number): HTMLElement | null {
    const elements = Array.from(this.shadowRoot?.querySelectorAll<HTMLElement>(selector) || [])
      .filter((element) => containsPoint(element, clientX, clientY))
      .sort((left, right) => {
        const depthDelta = getDepth(right) - getDepth(left);
        if (depthDelta !== 0) {
          return depthDelta;
        }

        return getArea(left) - getArea(right);
      });

    return elements[0] || null;
  }

  private _getParentLayout(documentModel: QdsContentDocument, parentBlockId: string | null): 'flow' | 'grid' {
    if (!parentBlockId) {
      return 'flow';
    }

    const parentBlock = findBlockById(documentModel.blocks, parentBlockId, 'page');
    const parentDefinition = parentBlock ? this._getRegistry().get(parentBlock.type) : undefined;
    return getBlockChildOptions(parentDefinition)?.layout || 'flow';
  }

  private _isAncestorTarget(
    documentModel: QdsContentDocument,
    blockId: string,
    parentBlockId: string | null
  ): boolean {
    let currentParentId = parentBlockId;
    while (currentParentId) {
      if (currentParentId === blockId) {
        return true;
      }

      const currentLocation = findBlockTreeLocation(documentModel.blocks, currentParentId, 'page');
      currentParentId = currentLocation?.parentBlockId || null;
    }

    return false;
  }

  private _canDropPayloadAtTarget(
    payload: QdsContentDragPayload,
    target: QdsBlockDropTarget,
    documentModel: QdsContentDocument
  ): boolean {
    const registry = this._getRegistry();
    const draggedType = payload.kind === 'palette-block'
      ? payload.type
      : findBlockById(documentModel.blocks, payload.blockId, 'page')?.type;

    if (!draggedType) {
      return false;
    }

    if (payload.kind === 'document-block' && this._isAncestorTarget(documentModel, payload.blockId, target.parentBlockId)) {
      return false;
    }

    if (!target.parentBlockId) {
      return true;
    }

    const parentBlock = findBlockById(documentModel.blocks, target.parentBlockId, 'page');
    if (!parentBlock) {
      return false;
    }

    const childOptions = getBlockChildOptions(registry.get(parentBlock.type));
    if (!childOptions) {
      return false;
    }

    return !childOptions.accepts || childOptions.accepts.includes(draggedType);
  }

  private _resolvePageDropDetail(
    clientX: number,
    clientY: number,
    payload: QdsContentDragPayload | null
  ): ResolvedDropDetail | null {
    const documentModel = this._getDocumentModel();
    if (!documentModel) {
      return null;
    }

    const candidates: ResolvedDropDetail[] = [];

    const tail = this._pickDeepestTarget('[data-tail-parent-id][data-tail-index]', clientX, clientY);
    if (tail) {
      const parentBlockId = decodeParentBlockId(tail.dataset.tailParentId);
      const index = toIndex(tail.dataset.tailIndex);
      if (index !== null) {
        const detail: ResolvedDropDetail = {
          signature: `tail:${encodeParentBlockId(parentBlockId)}:${index}`,
          target: {
            parentBlockId,
            index
          },
          kind: 'tail',
          element: tail
        };
        if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
          candidates.push(detail);
        }
      }
    }

    const empty = this._pickDeepestTarget('[data-empty-parent-id][data-empty-index]', clientX, clientY);
    if (empty) {
      const parentBlockId = decodeParentBlockId(empty.dataset.emptyParentId);
      const index = toIndex(empty.dataset.emptyIndex);
      if (index !== null) {
        const detail: ResolvedDropDetail = {
          signature: `empty:${encodeParentBlockId(parentBlockId)}:${index}`,
          target: {
            parentBlockId,
            index
          },
          kind: 'empty',
          element: empty
        };
        if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
          candidates.push(detail);
        }
      }
    }

    const slot = this._pickDeepestTarget('[data-slot-parent-id][data-slot-index]', clientX, clientY);
    if (slot) {
      const parentBlockId = decodeParentBlockId(slot.dataset.slotParentId);
      const index = toIndex(slot.dataset.slotIndex);
      if (index !== null) {
        const layout = this._getParentLayout(documentModel, parentBlockId);
        const rect = slot.getBoundingClientRect();
        const insertBefore = layout === 'grid'
          ? clientX <= rect.left + (rect.width / 2)
          : clientY <= rect.top + (rect.height / 2);
        const detail: ResolvedDropDetail = {
          signature: `slot:${encodeParentBlockId(parentBlockId)}:${index}:${insertBefore ? 'before' : 'after'}`,
          target: {
            parentBlockId,
            index: insertBefore ? index : index + 1
          },
          kind: insertBefore ? 'before' : 'after',
          element: slot
        };
        if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
          candidates.push(detail);
        }
      }
    }

    const container = this._pickDeepestTarget('[data-drop-into-parent-id][data-container-child-count]', clientX, clientY);
    if (container) {
      const parentBlockId = container.dataset.dropIntoParentId || null;
      const index = toIndex(container.dataset.containerChildCount);
      if (parentBlockId && index !== null) {
        const detail: ResolvedDropDetail = {
          signature: `container:${parentBlockId}:${index}`,
          target: {
            parentBlockId,
            index
          },
          kind: 'container',
          element: container
        };
        if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
          candidates.push(detail);
        }
      }
    }

    return candidates.sort((left, right) => {
      const depthDelta = getDepth(right.element) - getDepth(left.element);
      if (depthDelta !== 0) {
        return depthDelta;
      }

      return getDropDetailPriority(left) - getDropDetailPriority(right);
    })[0] || null;
  }

  private _resolveDashboardDropDetail(
    payload: QdsContentDragPayload | null
  ): ResolvedDropDetail | null {
    const documentModel = this._getDocumentModel();
    if (!documentModel) {
      return null;
    }

    const dashboard = this.shadowRoot?.querySelector<HTMLElement>('[data-dashboard-target-index]');
    if (!dashboard) {
      return null;
    }

    const index = toIndex(dashboard.dataset.dashboardTargetIndex);
    if (index === null) {
      return null;
    }

    const detail: ResolvedDropDetail = {
      signature: `dashboard:${index}`,
      target: {
        parentBlockId: null,
        index
      },
      kind: 'dashboard',
      element: dashboard
    };

    if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
      return detail;
    }

    return null;
  }

  private _resolveDropDetail(
    clientX: number,
    clientY: number,
    payload: QdsContentDragPayload | null,
    eventTarget?: EventTarget | null
  ): ResolvedDropDetail | null {
    const documentModel = this._getDocumentModel();
    if (!documentModel) {
      return null;
    }

    const targetElement = eventTarget instanceof HTMLElement ? eventTarget : null;
    if (targetElement) {
      const candidates: ResolvedDropDetail[] = [];

      const tail = targetElement.closest<HTMLElement>('[data-tail-parent-id][data-tail-index]');
      if (tail) {
        const parentBlockId = decodeParentBlockId(tail.dataset.tailParentId);
        const index = toIndex(tail.dataset.tailIndex);
        if (index !== null) {
          const detail: ResolvedDropDetail = {
            signature: `tail:${encodeParentBlockId(parentBlockId)}:${index}`,
            target: {
              parentBlockId,
              index
            },
            kind: 'tail',
            element: tail
          };
          if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
            candidates.push(detail);
          }
        }
      }

      const empty = targetElement.closest<HTMLElement>('[data-empty-parent-id][data-empty-index]');
      if (empty) {
        const parentBlockId = decodeParentBlockId(empty.dataset.emptyParentId);
        const index = toIndex(empty.dataset.emptyIndex);
        if (index !== null) {
          const detail: ResolvedDropDetail = {
            signature: `empty:${encodeParentBlockId(parentBlockId)}:${index}`,
            target: {
              parentBlockId,
              index
            },
            kind: 'empty',
            element: empty
          };
          if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
            candidates.push(detail);
          }
        }
      }

      const slot = targetElement.closest<HTMLElement>('[data-slot-parent-id][data-slot-index]');
      if (slot) {
        const parentBlockId = decodeParentBlockId(slot.dataset.slotParentId);
        const index = toIndex(slot.dataset.slotIndex);
        if (index !== null) {
          const layout = this._getParentLayout(documentModel, parentBlockId);
          const rect = slot.getBoundingClientRect();
          const insertBefore = layout === 'grid'
            ? clientX <= rect.left + (rect.width / 2)
            : clientY <= rect.top + (rect.height / 2);
          const detail: ResolvedDropDetail = {
            signature: `slot:${encodeParentBlockId(parentBlockId)}:${index}:${insertBefore ? 'before' : 'after'}`,
            target: {
              parentBlockId,
              index: insertBefore ? index : index + 1
            },
            kind: insertBefore ? 'before' : 'after',
            element: slot
          };
          if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
            candidates.push(detail);
          }
        }
      }

      const container = targetElement.closest<HTMLElement>('[data-drop-into-parent-id][data-container-child-count]');
      if (container) {
        const parentBlockId = container.dataset.dropIntoParentId || null;
        const index = toIndex(container.dataset.containerChildCount);
        if (parentBlockId && index !== null) {
          const detail: ResolvedDropDetail = {
            signature: `container:${parentBlockId}:${index}`,
            target: {
              parentBlockId,
              index
            },
            kind: 'container',
            element: container
          };
          if (!payload || this._canDropPayloadAtTarget(payload, detail.target, documentModel)) {
            candidates.push(detail);
          }
        }
      }

      const bestCandidate = candidates.sort((left, right) => {
        const depthDelta = getDepth(right.element) - getDepth(left.element);
        if (depthDelta !== 0) {
          return depthDelta;
        }

        return getDropDetailPriority(left) - getDropDetailPriority(right);
      })[0];
      if (bestCandidate) {
        return bestCandidate;
      }

      const dashboard = targetElement.closest<HTMLElement>('[data-dashboard-target-index]');
      if (dashboard) {
        const index = toIndex(dashboard.dataset.dashboardTargetIndex);
        if (index !== null) {
          return {
            signature: `dashboard:${index}`,
            target: {
              parentBlockId: null,
              index
            },
            kind: 'dashboard',
            element: dashboard
          };
        }
      }
    }

    if (documentModel.mode === 'dashboard') {
      return this._resolveDashboardDropDetail(payload);
    }

    return this._resolvePageDropDetail(clientX, clientY, payload);
  }

  private _setActiveDropDetail(nextDetail: ResolvedDropDetail | null): void {
    if (this._activeDropDetail?.signature === nextDetail?.signature) {
      return;
    }

    if (this._activeDropDetail) {
      if (this._activeDropDetail.kind === 'before') {
        this._activeDropDetail.element.classList.remove('canvas__slot--drop-before');
      } else if (this._activeDropDetail.kind === 'after') {
        this._activeDropDetail.element.classList.remove('canvas__slot--drop-after');
      } else if (this._activeDropDetail.kind === 'container') {
        this._activeDropDetail.element.classList.remove('canvas__container--active');
      } else if (this._activeDropDetail.kind === 'dashboard') {
        this._activeDropDetail.element.classList.remove('canvas__dashboard--active');
      } else {
        this._activeDropDetail.element.classList.remove('canvas__drop-zone--active');
      }
    }

    this._activeDropDetail = nextDetail;

    if (!nextDetail) {
      clearActiveContentDragHover(this._dragOwnerId);
      this.removeAttribute('drag-active');
      return;
    }

    if (nextDetail.kind === 'before') {
      nextDetail.element.classList.add('canvas__slot--drop-before');
    } else if (nextDetail.kind === 'after') {
      nextDetail.element.classList.add('canvas__slot--drop-after');
    } else if (nextDetail.kind === 'container') {
      nextDetail.element.classList.add('canvas__container--active');
    } else if (nextDetail.kind === 'dashboard') {
      nextDetail.element.classList.add('canvas__dashboard--active');
    } else {
      nextDetail.element.classList.add('canvas__drop-zone--active');
    }

    setActiveContentDragHover(this._dragOwnerId, nextDetail.target);
    this.setAttribute('drag-active', '');
  }

  private _assignFrames(documentModel: QdsContentDocument, registry: QdsBlockRegistry): void {
    const frames = this.shadowRoot?.querySelectorAll<QdsContentBlockFrame>('qds-content-block-frame') || [];

    frames.forEach((frame) => {
      const blockId = frame.dataset.blockId;
      const block = blockId ? findBlockById(documentModel.blocks, blockId, 'page') : null;
      if (!block) {
        return;
      }

      frame.block = block;
      frame.definition = registry.get(block.type);
      frame.contentDocument = documentModel;
      frame.documentMode = documentModel.mode;
      frame.registry = registry;
      frame.context = this._context || { blockRegistry: registry };
      frame.selected = block.id === this._selectedBlockId;
      frame.readonly = this._readonly;
      frame.style.opacity = block.visibility?.hidden ? '0.72' : '1';
    });
  }

  private _emitBlockDrop(detail: QdsBlockDropDetail): void {
    this.dispatchEvent(new CustomEvent<QdsBlockDropDetail>('block-drop', {
      bubbles: true,
      composed: true,
      detail
    }));
  }

  private _handleDragOver = (event: DragEvent): void => {
    if (this._readonly || !event.dataTransfer) {
      return;
    }

    const payload = getContentDragPayload(event) || getActiveContentDragPayload();
    if (!payload) {
      return;
    }

    const detail = this._resolveDropDetail(event.clientX, event.clientY, payload, event.target);
    if (!detail) {
      this._setActiveDropDetail(null);
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = payload.kind === 'palette-block' ? 'copy' : 'move';
    this._setActiveDropDetail(detail);
  };

  private _handleDrop = (event: DragEvent): void => {
    if (this._readonly) {
      return;
    }

    const payload = getContentDragPayload(event) || getActiveContentDragPayload();
    const detail = this._resolveDropDetail(event.clientX, event.clientY, payload, event.target);
    this._setActiveDropDetail(null);

    if (!payload || !detail) {
      return;
    }

    event.preventDefault();
    clearActiveContentDragState();
    this._emitBlockDrop({
      item: payload.kind === 'palette-block'
        ? { kind: 'new', type: payload.type }
        : { kind: 'existing', blockId: payload.blockId },
      target: detail.target
    });
  };

  private _handleGlobalDragEnd = (): void => {
    const hover = getActiveContentDragHover();
    const payload = getActiveContentDragPayload();
    if (hover?.ownerId === this._dragOwnerId && payload) {
      this._emitBlockDrop({
        item: payload.kind === 'palette-block'
          ? { kind: 'new', type: payload.type }
          : { kind: 'existing', blockId: payload.blockId },
        target: hover.target
      });
      clearActiveContentDragState();
    }

    this._setActiveDropDetail(null);
  };

  private _clearPointerDragState(): void {
    this._pointerDragBlockId = null;
    this._pointerDragStartX = null;
    this._pointerDragStartY = null;
    this._setActiveDropDetail(null);
  }

  private _biasPointerDropDetail(
    detail: ResolvedDropDetail | null,
    clientX: number,
    clientY: number
  ): ResolvedDropDetail | null {
    const documentModel = this._getDocumentModel();
    if (!detail || !documentModel || !this._pointerDragBlockId) {
      return detail;
    }

    const sourceLocation = findBlockTreeLocation(documentModel.blocks, this._pointerDragBlockId, 'page');
    if (!sourceLocation) {
      return detail;
    }

    const slotParentId = decodeParentBlockId(detail.element.dataset.slotParentId);
    const slotIndex = toIndex(detail.element.dataset.slotIndex);
    if (slotIndex === null || slotParentId !== sourceLocation.parentBlockId || slotIndex !== sourceLocation.index) {
      return detail;
    }

    const layout = this._getParentLayout(documentModel, sourceLocation.parentBlockId);
    if (layout === 'grid' && this._pointerDragStartX !== null) {
      const moveForward = clientX > this._pointerDragStartX;
      return {
        ...detail,
        signature: `slot:${encodeParentBlockId(sourceLocation.parentBlockId)}:${sourceLocation.index}:${moveForward ? 'after' : 'before'}`,
        target: {
          parentBlockId: sourceLocation.parentBlockId,
          index: moveForward ? sourceLocation.index + 1 : sourceLocation.index
        },
        kind: moveForward ? 'after' : 'before'
      };
    }

    if (layout === 'flow' && this._pointerDragStartY !== null) {
      const moveForward = clientY > this._pointerDragStartY;
      return {
        ...detail,
        signature: `slot:${encodeParentBlockId(sourceLocation.parentBlockId)}:${sourceLocation.index}:${moveForward ? 'after' : 'before'}`,
        target: {
          parentBlockId: sourceLocation.parentBlockId,
          index: moveForward ? sourceLocation.index + 1 : sourceLocation.index
        },
        kind: moveForward ? 'after' : 'before'
      };
    }

    return detail;
  }

  private _handlePointerDragEvent = (event: CustomEvent): void => {
    const documentModel = this._getDocumentModel();
    if (this._readonly || documentModel?.mode !== 'page') {
      this._clearPointerDragState();
      return;
    }

    switch (event.type) {
      case 'block-pointer-drag-start':
        this._pointerDragBlockId = event.detail.blockId || null;
        this._pointerDragStartX = typeof event.detail.startX === 'number' ? event.detail.startX : null;
        this._pointerDragStartY = typeof event.detail.startY === 'number' ? event.detail.startY : null;
        break;
      case 'block-pointer-drag-move': {
        if (!this._pointerDragBlockId) {
          return;
        }

        const payload: QdsContentDragPayload = {
          kind: 'document-block',
          blockId: this._pointerDragBlockId,
          mode: 'page'
        };
        const detail = this._biasPointerDropDetail(
          this._resolveDropDetail(event.detail.clientX, event.detail.clientY, payload),
          event.detail.clientX,
          event.detail.clientY
        );
        this._setActiveDropDetail(detail);
        break;
      }
      case 'block-pointer-drag-end':
        if (this._pointerDragBlockId && this._activeDropDetail) {
          this._emitBlockDrop({
            item: {
              kind: 'existing',
              blockId: this._pointerDragBlockId
            },
            target: this._activeDropDetail.target
          });
        }
        this._clearPointerDragState();
        break;
      default:
        break;
    }
  };

  private _renderPageFlow(
    blocks: QdsContentDocument['blocks'],
    parentBlockId: string | null,
    depth: number,
    emptyLabel: string
  ): string {
    const orderedBlocks = sortBlocksForMode(blocks, 'page');
    const encodedParentId = encodeParentBlockId(parentBlockId);

    if (orderedBlocks.length === 0) {
      return `
        <div
          class="canvas__empty ${parentBlockId ? 'canvas__empty--nested' : ''}"
          data-empty-parent-id="${encodedParentId}"
          data-empty-index="0"
          data-depth="${depth}"
        >
          ${emptyLabel}
        </div>
      `;
    }

    return `
      <div class="${parentBlockId ? 'canvas__children' : 'canvas__flow'}" data-depth="${depth}">
        ${orderedBlocks.map((block, index) => this._renderPageBlock(block, parentBlockId, index, depth)).join('')}
        <div
          class="canvas__tail canvas__drop-zone ${parentBlockId ? 'canvas__tail--nested' : ''}"
          data-tail-parent-id="${encodedParentId}"
          data-tail-index="${orderedBlocks.length}"
          data-depth="${depth}"
        >
          Drop here to place at the end.
        </div>
      </div>
    `;
  }

  private _renderPageBlock(
    block: QdsContentDocument['blocks'][number],
    parentBlockId: string | null,
    index: number,
    depth: number
  ): string {
    const registry = this._getRegistry();
    const definition = registry.get(block.type);
    const childOptions = getBlockChildOptions(definition);
    const childBlocks = getBlockChildren(block, 'page');
    const childColumns = Math.max(1, Math.min(4, Number(block.config.columns) || 3));

    return `
      <div
        class="canvas__slot"
        data-slot-parent-id="${encodeParentBlockId(parentBlockId)}"
        data-slot-index="${index}"
        data-depth="${depth}"
      >
        <qds-content-block-frame
          class="canvas__frame"
          data-block-id="${block.id}"
          ${childOptions ? 'has-nested-content=""' : ''}
        >
          ${childOptions ? `
            <div
              slot="nested-content"
              class="canvas__nested-shell canvas__nested-shell--${childOptions.layout || 'flow'}"
              style="${childOptions.layout === 'grid' ? `--canvas-child-columns:${childColumns};` : ''}"
              data-drop-into-parent-id="${block.id}"
              data-container-child-count="${childBlocks.length}"
              data-depth="${depth + 1}"
            >
              ${this._renderPageFlow(
                childBlocks,
                block.id,
                depth + 1,
                childOptions.emptyLabel || 'Drop components here.'
              )}
            </div>
          ` : ''}
        </qds-content-block-frame>
      </div>
    `;
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    if (!this._document) {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
          }
        </style>
        <div>No content document is loaded.</div>
      `;
      return;
    }

    const registry = this._getRegistry();
    const documentModel = normalizeContentDocument(this._document, registry);
    const blocks = sortBlocksForMode(documentModel.blocks, documentModel.mode);
    const showGrid = documentModel.mode === 'dashboard' && documentModel.canvas?.showGrid;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          color: var(--theme-text-primary, #333333);
          font-family: var(--font-family-primary, 'IBM Plex Sans', sans-serif);
        }

        .canvas {
          display: block;
        }

        .canvas__surface {
          display: grid;
          gap: 20px;
          padding: 20px;
          border-radius: 0;
          background: var(--theme-background-secondary, #f8f8f8);
        }

        .canvas__body {
          padding: 20px 24px;
          border: 1px solid var(--theme-border-color, #e0e0e0);
          border-radius: 12px;
          background: var(--theme-background-primary, #ffffff);
        }

        .canvas__body > :first-child {
          margin-top: 0;
        }

        .canvas__body > :last-child {
          margin-bottom: 0;
        }

        .canvas__flow,
        .canvas__children {
          display: grid;
          gap: 16px;
        }

        .canvas__slot {
          position: relative;
          display: grid;
          min-width: 0;
        }

        .canvas__slot--drop-before::before,
        .canvas__slot--drop-after::after {
          content: '';
          position: absolute;
          left: 8px;
          right: 8px;
          height: 3px;
          border-radius: 999px;
          background: var(--input-focus-border, #4285f4);
          box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.14);
          pointer-events: none;
          z-index: 2;
        }

        .canvas__slot--drop-before::before {
          top: -8px;
        }

        .canvas__slot--drop-after::after {
          bottom: -8px;
        }

        .canvas__frame {
          min-width: 0;
        }

        .canvas__nested-shell {
          display: grid;
          gap: 12px;
          padding: 14px;
          border-top: 1px solid var(--theme-border-color, #e0e0e0);
          background: rgba(0, 0, 0, 0.02);
          transition: background-color 0.2s ease, box-shadow 0.2s ease;
        }

        .canvas__nested-shell--grid .canvas__children {
          grid-template-columns: repeat(var(--canvas-child-columns, 3), minmax(0, 1fr));
        }

        .canvas__nested-shell--grid .canvas__tail,
        .canvas__nested-shell--grid .canvas__empty {
          grid-column: 1 / -1;
        }

        .canvas__container--active {
          background: rgba(66, 133, 244, 0.08);
          box-shadow: inset 0 0 0 1px var(--input-focus-border, #4285f4);
        }

        .canvas__dashboard {
          display: grid;
          gap: ${documentModel.layout?.gap || '16px'};
          grid-template-columns: repeat(${documentModel.layout?.columns || 12}, minmax(0, 1fr));
          grid-auto-rows: ${documentModel.layout?.rowHeight || 128}px;
          align-items: start;
          ${showGrid ? `
            background-image:
              linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
            background-size:
              calc(100% / ${documentModel.layout?.columns || 12}) 100%,
              100% ${documentModel.layout?.rowHeight || 128}px;
            background-position: top left;
          ` : ''}
        }

        .canvas__dashboard[data-dashboard-target-index] {
          border-radius: 12px;
          transition: outline-color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
        }

        .canvas__dashboard--active {
          border-color: var(--input-focus-border, #4285f4) !important;
          background: rgba(66, 133, 244, 0.12) !important;
        }

        .canvas__empty,
        .canvas__tail {
          min-height: 44px;
          padding: 16px;
          border: 1px dashed transparent;
          border-radius: 12px;
          color: transparent;
          display: grid;
          place-items: center;
          text-align: center;
          transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
        }

        .canvas__empty {
          color: var(--theme-text-secondary, #5f6368);
          border-color: var(--theme-border-color, #e0e0e0);
          background: var(--theme-background-primary, #ffffff);
        }

        .canvas__empty--nested {
          min-height: 64px;
          border-style: dashed;
          background: rgba(255, 255, 255, 0.72);
        }

        :host([drag-active]) .canvas__tail,
        :host([drag-active]) .canvas__dashboard[data-dashboard-target-index] {
          border-color: rgba(66, 133, 244, 0.28);
          background: rgba(66, 133, 244, 0.04);
          color: var(--theme-text-secondary, #5f6368);
        }

        .canvas__drop-zone--active {
          border-color: var(--input-focus-border, #4285f4) !important;
          background: rgba(66, 133, 244, 0.12) !important;
          color: var(--theme-text-secondary, #5f6368) !important;
        }

        @media (max-width: 1200px) {
          .canvas__dashboard {
            grid-template-columns: repeat(6, minmax(0, 1fr));
          }

          .canvas__nested-shell--grid .canvas__children {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 720px) {
          .canvas__surface {
            padding: 16px;
          }

          .canvas__dashboard {
            grid-template-columns: repeat(1, minmax(0, 1fr));
            grid-auto-rows: auto;
          }

          .canvas__frame {
            grid-column: auto !important;
            grid-row: auto !important;
          }

          .canvas__nested-shell--grid .canvas__children {
            grid-template-columns: minmax(0, 1fr);
          }
        }
      </style>
      <section class="canvas">
        <div class="canvas__surface" data-mode="${documentModel.mode}">
          ${documentModel.bodyHtml ? `<div class="canvas__body">${sanitizeHtml(documentModel.bodyHtml)}</div>` : ''}
          ${documentModel.mode === 'dashboard'
            ? `<div class="canvas__dashboard" ${!this._readonly ? `data-dashboard-target-index="${blocks.length}"` : ''}>
                ${blocks.map((block) => `
                  <qds-content-block-frame
                    class="canvas__frame"
                    data-block-id="${block.id}"
                    style="grid-column: ${Math.max(1, (block.layout?.x || 0) + 1)} / span ${Math.max(1, block.layout?.w || 1)}; grid-row: ${Math.max(1, (block.layout?.y || 0) + 1)} / span ${Math.max(1, block.layout?.h || 1)};"
                  ></qds-content-block-frame>
                `).join('')}
              </div>`
            : this._renderPageFlow(
                blocks,
                null,
                0,
                'Drag a block from the palette to start this document.'
              )}
        </div>
      </section>
    `;

    this._assignFrames(documentModel, registry);
  }
}

if (!customElements.get('qds-content-canvas')) {
  customElements.define('qds-content-canvas', QdsContentCanvas);
}

export default QdsContentCanvas;
